/**
 * RUBBY Analytics Service
 *
 * Provides a unified analytics API. Currently supports GA4.
 * Add Sentry, Hotjar, or other providers by extending this service.
 */

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export function initializeAnalytics() {
  if (!GA_MEASUREMENT_ID) {
    console.warn('[Analytics] No GA_MEASUREMENT_ID configured');
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
  });
}

export function trackPageView(path: string, title?: string) {
  if (!window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
}

export function trackEvent(eventName: string, params?: Record<string, any>) {
  if (!window.gtag) return;
  window.gtag('event', eventName, params);
}

export function trackWizardStep(step: number, stepId: string, wizardType: 'toy' | 'storybook' | 'animation') {
  trackEvent('wizard_step_completed', {
    wizard_type: wizardType,
    step_number: step,
    step_id: stepId,
  });
}

export function trackWizardAbandonment(step: number, stepId: string, wizardType: 'toy' | 'storybook' | 'animation', durationMs: number) {
  trackEvent('wizard_abandoned', {
    wizard_type: wizardType,
    step_number: step,
    step_id: stepId,
    session_duration_ms: durationMs,
  });
}

export function trackFileUpload(fileType: string, fileSizeKb: number) {
  trackEvent('file_upload', {
    file_type: fileType,
    file_size_kb: fileSizeKb,
  });
}

export function trackAIGeneration(type: 'character' | 'model' | 'storybook', status: 'started' | 'completed' | 'failed') {
  trackEvent('ai_generation', {
    generation_type: type,
    status,
  });
}

export function trackPurchase(orderId: string, totalCents: number, currency: string = 'USD') {
  trackEvent('purchase', {
    transaction_id: orderId,
    value: totalCents / 100,
    currency,
    items: [{
      item_name: 'Custom 3D Toy',
      item_category: 'Toys',
      price: totalCents / 100,
      quantity: 1,
    }],
  });
}

export function trackCheckoutStarted(totalCents: number) {
  trackEvent('begin_checkout', {
    value: totalCents / 100,
    currency: 'USD',
  });
}

export function identifyUser(userId: string) {
  if (!window.gtag) return;
  window.gtag('set', { user_id: userId });
}
